import { apiClient } from '@/api/client'
import type { PrintJobStatus, PrintJobStatusResponse, RelayedPrintJob } from '@/types'
import { desktop } from '@/lib/printTransport'
import { toastHelpers } from '@/lib/toast-helpers'

/**
 * Client side of the counter print relay (F-FE-N60).
 *
 * A tablet cannot reach the counter's USB printer, so it asks the backend to
 * queue counter-bound slips and the counter's desktop app prints them. This
 * module answers the two questions the tablet has: should I relay, and did the
 * paper actually come out?
 */

/**
 * Whether this client should ask the backend to relay counter-bound slips.
 *
 * True for everything that is not the desktop app — a tablet, a phone, the
 * Server APK's WebView, a browser. The desktop counter keeps printing its own
 * paper exactly as it always has, which is what keeps this change off the
 * critical path for the tills already in service.
 */
export function shouldRelayCounterPrint(): boolean {
  return !desktop()
}

/**
 * How long to wait for the counter to confirm before warning the waiter.
 *
 * Long enough to cover a poll interval plus a slow spool, short enough that a
 * waiter still standing at the table finds out. The alternative — staying quiet
 * — is the failure this whole feature exists to avoid: someone believing the
 * kitchen has a ticket it never received.
 */
export const WARN_AFTER_MS = 20000

/**
 * Poll quickly for this long — the common case is paper within a few seconds.
 * After it, poll slowly until WATCH_UNTIL_MS so a job the backend expires can
 * still be reported.
 */
export const FAST_POLL_FOR_MS = 45000

/**
 * Stop watching. The backend never prints a relay job older than 10 minutes
 * (printJobMaxAge, F-BE-N79), so by now every job is printed, failed or
 * expired; the extra minute covers the poll that observes the expiry.
 */
export const WATCH_UNTIL_MS = 11 * 60 * 1000

export const POLL_MS = 3000
export const SLOW_POLL_MS = 15000

/**
 * A print station that has not polled for this long counts as offline.
 * The backend only reports seconds-since-last-poll; this threshold is decided
 * here. The agent polls every 3 s (15 s when its connection is failing).
 */
export const STATION_ONLINE_WINDOW_SECONDS = 30

/** Waiter-facing copy (F-BE-N79). Exported so tests pin the wording. */
export const RELAY_MESSAGES = {
  stationOfflineTitle: 'No print station is online',
  stationOfflineBody: 'This ticket will not print until the counter PC is open.',
  expiredTitle: 'Not printed — print station offline',
  expiredBody: 'Use Reprint KOT once the counter PC is back.',
} as const

function isSettled(s: PrintJobStatus): boolean {
  return s.status === 'printed' || s.status === 'failed' || s.status === 'expired'
}

/**
 * Whether the backend says no print station is polling.
 *
 * Only a POSITIVE signal counts: a backend older than F-BE-N79 sends no station
 * fields (undefined), and a backend that restarted moments ago has not had a
 * poll yet (null with little tracking time). Neither may raise the warning — a
 * false "no print station" alarm teaches waiters to ignore the real one.
 */
export function isPrintStationOffline(res: PrintJobStatusResponse | undefined | null): boolean {
  if (!res) return false
  const seen = res.station_last_seen_seconds
  if (typeof seen === 'number') return seen > STATION_ONLINE_WINDOW_SECONDS
  if (seen === null) {
    const tracking = res.station_tracking_seconds
    return typeof tracking === 'number' && tracking > STATION_ONLINE_WINDOW_SECONDS
  }
  return false
}

/**
 * Watch relayed slips and tell the waiter what happened.
 *
 * Deliberately toasts rather than blocking: the waiter's job is to keep serving,
 * and the common case is that paper appears at the counter a second or two
 * later. The warnings only fire when it genuinely hasn't — and the first one
 * fires immediately when the backend already knows no print station is polling
 * (Karo, 2026-10-02: 96 of 97 tickets queued to a counter PC whose print agent
 * had been switched off by a reinstall, with every tablet showing "sent").
 *
 * Fire-and-forget — callers `void` this.
 */
export async function watchRelayedPrintJobs(jobs: RelayedPrintJob[] | undefined): Promise<void> {
  const ids = (jobs ?? []).map((j) => j.id).filter(Boolean)
  if (ids.length === 0) return

  const stations = [...new Set((jobs ?? []).map((j) => j.station_name).filter(Boolean))]
  const stationList = stations.join(', ')
  const startedAt = Date.now()

  // Ask once, immediately, whether anyone is there to print it.
  let offlineAtFire = false
  try {
    const res = await apiClient.getPrintJobStatus(ids)
    offlineAtFire = isPrintStationOffline(res.data)
  } catch {
    // Can't tell — fall through to the optimistic message; the poll loop below
    // still reports the real outcome.
  }

  if (offlineAtFire) {
    toastHelpers.warning(
      RELAY_MESSAGES.stationOfflineTitle,
      stationList
        ? `${stationList} — ${RELAY_MESSAGES.stationOfflineBody}`
        : RELAY_MESSAGES.stationOfflineBody,
    )
  } else {
    toastHelpers.success(
      'Sent to the counter printer',
      stationList ? `${stationList} — waiting for the counter to print.` : undefined,
    )
  }

  let warned = offlineAtFire

  for (;;) {
    const sinceStart = Date.now() - startedAt
    await sleep(sinceStart < FAST_POLL_FOR_MS ? POLL_MS : SLOW_POLL_MS)
    const elapsed = Date.now() - startedAt

    let statuses: PrintJobStatus[] = []
    try {
      const res = await apiClient.getPrintJobStatus(ids)
      statuses = res.data?.jobs ?? []
    } catch {
      // Network hiccup on the tablet. Keep waiting — the job is safe in the
      // queue regardless of whether we can see it.
      if (elapsed >= WATCH_UNTIL_MS) return
      continue
    }

    const failed = statuses.filter((s) => s.status === 'failed')
    if (failed.length > 0) {
      toastHelpers.error(
        'The counter printer rejected a ticket',
        `${failed.map((f) => f.station_name).join(', ')} — take the order to the kitchen and check the counter printer.`,
      )
      return
    }

    const expired = statuses.filter((s) => s.status === 'expired')
    if (expired.length > 0) {
      const names = [...new Set(expired.map((e) => e.station_name).filter(Boolean))].join(', ')
      toastHelpers.error(
        RELAY_MESSAGES.expiredTitle,
        names ? `${names} — ${RELAY_MESSAGES.expiredBody}` : RELAY_MESSAGES.expiredBody,
      )
      return
    }

    if (statuses.length === ids.length && statuses.every(isSettled)) {
      toastHelpers.success('Printed at the counter', stationList || undefined)
      return
    }

    if (!warned && elapsed >= WARN_AFTER_MS) {
      warned = true
      toastHelpers.warning(
        'The counter has not printed this yet',
        'It prints only if the counter PC picks it up within 10 minutes. Check the counter before assuming the kitchen has it.',
      )
    }

    if (elapsed >= WATCH_UNTIL_MS) return
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
